/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ButtonWithTooltip
 *
 * @file ButtonWithTooltip.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import mixins from '../../assets/styles/mixins.scss';

const StyledTooltip = styled(({ className, popperProps, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} slotProps={{ popper: popperProps }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 8px',
    gap: 12,
    alignItems: 'flex-start',
    borderRadius: '4px',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    background: mixins.chalkWhiteFont,
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    boxShadow: `0 1px 3px 0 ${mixins.color_elevation_alpha_04}, 0 1px 1px 0 ${mixins.color_elevation_alpha_08}, 0 2px 1px -1px ${mixins.color_elevation_alpha_12}`
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: mixins.chalkWhiteFont,
    '&::before': {
      border: '1px solid rgba(13, 0, 77, 0.15)',
      boxShadow: `0 1px 3px 0 ${mixins.color_elevation_alpha_04}, 0 1px 1px 0 ${mixins.color_elevation_alpha_08}, 0 2px 1px -1px ${mixins.color_elevation_alpha_12}`
    }
  }
});

const ButtonWithTooltip = (props) => {
  const {
    children,
    tooltip,
    placement,
    showTooltip,
    popperProps
  } = props;

  if (!tooltip || !showTooltip) {
    return children;
  }

  return (
    <StyledTooltip
      title={tooltip}
      arrow
      placement={placement}
      popperProps={popperProps}
    >
      {children}
    </StyledTooltip>
  );
};

ButtonWithTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  tooltip: PropTypes.string.isRequired,
  placement: PropTypes.string,
  showTooltip: PropTypes.bool,
  popperProps: PropTypes.object
};

ButtonWithTooltip.defaultProps = {
  placement: 'bottom',
  showTooltip: true,
  popperProps: null
};

export default ButtonWithTooltip;
